// 6b. 演目設定（管理者向け）。既存クラスのON/OFF（申込ページ掲載）＋特別演目の追加/削除。
// ここで作った演目セット（show_on_entry=true）が申込フォーム/演目順/点呼表/音源照明の元になる（仕様 §1⑥b）。
// 🟦9: 点呼表/タイムテーブルのデータ源も兼ねる。特別演目に非演目（進行行＝MC/休憩/写真撮影等は表示名で区別）を追加でき、
// 特別演目は行種別/表示名/所要時間を編集できる。出演順番号・部は演目順で設定・転換は基本設定で統一（§⑨ v1.9）。
import { Fragment, useEffect, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import StatusMessage from "../../../components/common/StatusMessage";
import {
  getRecitalPrograms,
  toggleRecitalClass,
  addRecitalSpecial,
  patchRecitalProgram,
  deleteRecitalProgram,
} from "../../../utils/recitalApi";
import "./recital.scss";

const RC_BREADCRUMBS = [
  { title: "Home", path: "/" },
  { title: "Recital", path: "/recital/2026/program" },
  { title: "Programs", path: "/recital/2026/program" },
];

// 行種別：演目(dance＝出演者あり)／その他(other＝進行行)の2択。MC・休憩・写真撮影等の具体名は表示名に入力。
const ENTRY_TYPES = [
  { value: "dance", label: "演目" },
  { value: "other", label: "その他" },
];
// 行種別ごとの演目名デフォルト（空欄時の補完）
const TYPE_DEFAULT_TITLE = { mc: "MC", break: "休憩", other: "" };

// 秒 → "M:SS"
const mmss = (sec) =>
  `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
// 分・秒（文字列）→ 秒。両方空なら null。
const durToSec = (min, sec) => {
  if ((min === "" || min == null) && (sec === "" || sec == null)) return null;
  return Number(min || 0) * 60 + Number(sec || 0);
};

// 演目行のタイムテーブル設定サマリー（設定済みのみ）
const timetableMeta = (p) => {
  const bits = [];
  if (p.duration_seconds != null) bits.push(`所要${mmss(p.duration_seconds)}`);
  return bits.join(" ・ ");
};

export default function RecitalProgramPage() {
  const [classes, setClasses] = useState([]); // {placement_id, program_id, display_title, note, show_on_entry, part, ...}
  const [specials, setSpecials] = useState([]); // {program_id, title, instructor_name, entry_type, part, ...}
  const [instructors, setInstructors] = useState([]); // {id, name}
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("dance");
  const [newInstructor, setNewInstructor] = useState("");
  const [newMin, setNewMin] = useState(""); // 進行行の所要（分）
  const [newSec, setNewSec] = useState(""); // 進行行の所要（秒）
  const [confirmDel, setConfirmDel] = useState(null);
  const [edit, setEdit] = useState(null); // 編集中の演目（モーダル）
  const [draft, setDraft] = useState(null); // 編集ドラフト
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");

  const notify = (text, type = "success") => {
    setMsgType(type);
    setMessage(text);
    setTimeout(() => setMessage(""), 3000);
  };

  const load = () =>
    getRecitalPrograms()
      .then((d) => {
        setClasses(d.classes || []);
        setSpecials(d.specials || []);
        setInstructors(d.instructors || []);
      })
      .catch((e) => notify(e.message || "読み込みに失敗しました", "error"));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PATCH 返却（program_id 一致行）を両リストへ反映
  const applyProgram = (prog) => {
    setClasses((list) =>
      list.map((x) =>
        x.program_id === prog.program_id ? { ...x, ...prog } : x,
      ),
    );
    setSpecials((list) =>
      list.map((x) =>
        x.program_id === prog.program_id ? { ...x, ...prog } : x,
      ),
    );
  };

  // 既存クラスの掲載トグル（upsert）
  const toggleClass = async (c) => {
    const next = !c.show_on_entry;
    try {
      const d = await toggleRecitalClass(c.placement_id, next);
      setClasses((list) =>
        list.map((x) => (x.placement_id === c.placement_id ? d.class : x)),
      );
    } catch (e) {
      notify(e.message || "更新に失敗しました", "error");
    }
  };

  // 特別演目の掲載トグル
  const toggleSpecial = async (s) => {
    try {
      const d = await patchRecitalProgram(s.program_id, {
        show_on_entry: !s.show_on_entry,
      });
      setSpecials((list) =>
        list.map((x) =>
          x.program_id === s.program_id
            ? { ...x, show_on_entry: d.program.show_on_entry }
            : x,
        ),
      );
    } catch (e) {
      notify(e.message || "更新に失敗しました", "error");
    }
  };

  // 行種別変更：演目名が空 or 既定値のままなら新しい既定値へ補完
  const changeNewType = (t) => {
    setNewType(t);
    const prevDefault = TYPE_DEFAULT_TITLE[newType] ?? "";
    if (newTitle === "" || newTitle === prevDefault) {
      setNewTitle(TYPE_DEFAULT_TITLE[t] ?? "");
    }
  };

  const addSpecial = async () => {
    const title = newTitle.trim();
    if (!title) return;
    // 演目（合同作品等）は担当（代表）必須
    if (newType === "dance" && !newInstructor) {
      notify("演目には担当講師が必須です", "error");
      return;
    }
    const extra = { entry_type: newType };
    if (newType !== "dance") {
      const sec = durToSec(newMin, newSec);
      if (sec != null) extra.duration_seconds = sec;
    }
    try {
      const d = await addRecitalSpecial(
        title,
        newType === "dance" ? newInstructor || null : null,
        extra,
      );
      setSpecials((list) => [...list, d.program]);
      setNewTitle("");
      setNewType("dance");
      setNewInstructor("");
      setNewMin("");
      setNewSec("");
    } catch (e) {
      notify(e.message || "追加に失敗しました", "error");
    }
  };

  const removeSpecial = async (s) => {
    try {
      await deleteRecitalProgram(s.program_id);
      setSpecials((list) => list.filter((x) => x.program_id !== s.program_id));
    } catch (e) {
      notify(e.message || "削除に失敗しました", "error");
    }
  };

  // --- タイムテーブル編集モーダル ---
  const openEdit = (p, isSpecial) => {
    setEdit({ ...p, _isSpecial: isSpecial });
    setDraft({
      entry_type: p.entry_type || "dance",
      instructor_id: p.instructor_id ? String(p.instructor_id) : "",
      title: p.title || p.display_title || "",
      durMin:
        p.duration_seconds == null
          ? ""
          : String(Math.floor(p.duration_seconds / 60)),
      durSec: p.duration_seconds == null ? "" : String(p.duration_seconds % 60),
    });
  };
  const closeEdit = () => {
    setEdit(null);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (busy || !edit || !draft) return;
    const patch = {};
    if (edit._isSpecial) {
      patch.entry_type = draft.entry_type;
      const t = draft.title.trim();
      if (!t) {
        notify("演目名は必須です", "error");
        return;
      }
      patch.title = t;
      if (draft.entry_type === "dance") {
        // 演目は担当（代表）必須。合同作品も誰かを担当に。
        if (!draft.instructor_id) {
          notify("演目には担当講師が必須です", "error");
          return;
        }
        patch.instructor_id = Number(draft.instructor_id);
      } else {
        const sec = durToSec(draft.durMin, draft.durSec);
        patch.duration_seconds = sec == null ? "" : sec;
      }
    }
    setBusy(true);
    try {
      const d = await patchRecitalProgram(edit.program_id, patch);
      applyProgram(d.program);
      closeEdit();
      notify("保存しました");
    } catch (e) {
      notify(e.message || "保存に失敗しました", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rc-page rc-page-wide">
      <Breadcrumbs breadcrumbs={RC_BREADCRUMBS} />
      <StatusMessage message={message} type={msgType} />
      <div className="rc-bar">
        <h3>演目設定</h3>
      </div>
      <p className="rc-lead">
        トグル
        ON＝申込ページに掲載／OFF＝掲載しない。特別演目は申込に出さなくても演目順には残せます。
        出演順番号・部は演目順（並び替え）で設定します。
      </p>

      <div className="rc-prog-layout" data-verify-recital-programs>
        <div className="rc-prog-main">
          {classes.map((c, i) => {
            const meta = timetableMeta(c);
            // 店舗が変わったら店舗名ラベルを差し込む（下線なし）
            const showStudio = i === 0 || c.studio !== classes[i - 1].studio;
            return (
              <Fragment key={c.placement_id}>
                {showStudio && (
                  <div className="rc-studio-head">{c.studio}</div>
                )}
                <div className={`rc-prog${c.show_on_entry ? "" : " off"}`}>
                  <div className="body">
                    <div className="ttl">{c.label || c.display_title}</div>
                    <div className="cast">{c.time_str || c.note}</div>
                    {meta && (
                      <div className="rc-tmeta" data-verify-tmeta={meta}>
                        {meta}
                      </div>
                    )}
                  </div>
                  <div
                    className={`rc-tgl ${c.show_on_entry ? "on" : ""}`}
                    data-verify-show={c.show_on_entry ? "on" : "off"}
                    onClick={() => toggleClass(c)}
                  />
                </div>
              </Fragment>
            );
          })}

          {specials.length > 0 && (
            <div className="rc-section">特別演目・進行</div>
          )}
          {specials.map((s) => {
            const isDance = (s.entry_type || "dance") === "dance";
            const meta = timetableMeta(s);
            return (
              <div
                key={s.program_id}
                className={`rc-prog special${s.show_on_entry || !isDance ? "" : " off"}`}
                data-verify-entry-type={s.entry_type || "dance"}
              >
                <div className="body">
                  <div className="ttl">
                    {s.label || s.title}
                    {isDance && (
                      <span className="rc-tag special" style={{ marginLeft: 8 }}>
                        特別演目
                      </span>
                    )}
                  </div>
                  <div className="cast">{isDance ? "" : "その他"}</div>
                  {meta && (
                    <div className="rc-tmeta" data-verify-tmeta={meta}>
                      {meta}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="rc-btn sm"
                  style={{ marginRight: 8 }}
                  onClick={() => openEdit(s, true)}
                >
                  編集
                </button>
                <button
                  className="rc-btn sm"
                  style={{ marginRight: 8 }}
                  onClick={() => setConfirmDel(s)}
                >
                  削除
                </button>
                {isDance ? (
                  <div
                    className={`rc-tgl ${s.show_on_entry ? "on" : ""}`}
                    onClick={() => toggleSpecial(s)}
                  />
                ) : (
                  // 非演目はトグルが無いぶん、編集/削除をdance行と揃えるためのスペーサー
                  <div className="rc-tgl-spacer" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </div>

        <aside className="rc-prog-side">
          <p className="ph">特別演目・進行を追加</p>
          <p className="psub">
            合同作品・オープニングのほか、MC・休憩・写真撮影など進行行も追加できます。
          </p>
          <label>種類</label>
          <select
            value={newType}
            onChange={(e) => changeNewType(e.target.value)}
            data-verify-add-type={newType}
          >
            {ENTRY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <label>{newType === "dance" ? "演目名" : "表示名"}</label>
          <input
            type="text"
            placeholder={
              newType === "dance"
                ? "例：オープニング合同作品"
                : "例：MC・休憩・写真撮影"
            }
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          {newType === "dance" ? (
            <>
              <label>担当講師</label>
              <select
                value={newInstructor}
                onChange={(e) => setNewInstructor(e.target.value)}
              >
                <option value="">担当講師を選択</option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label>所要時間（分・秒）</label>
              <div className="rc-dur">
                <input
                  type="number"
                  min="0"
                  placeholder="分"
                  value={newMin}
                  onChange={(e) => setNewMin(e.target.value)}
                />
                <span>:</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  placeholder="秒"
                  value={newSec}
                  onChange={(e) => setNewSec(e.target.value)}
                />
              </div>
            </>
          )}
          <button className="rc-btn pri" onClick={addSpecial}>
            ＋ 追加する
          </button>
        </aside>
      </div>

      {confirmDel && (
        <div className="rc-modal-ov" onClick={() => setConfirmDel(null)}>
          <div className="rc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mh">
              特別演目を削除
              <span className="x" onClick={() => setConfirmDel(null)}>
                ✕
              </span>
            </div>
            <div className="mb">
              <p style={{ margin: "0 0 6px", fontSize: 14 }}>
                「{confirmDel.title}」を削除します。
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#9aa1ad" }}>
                この操作は取り消せません。よろしいですか？
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button
                  className="rc-btn"
                  style={{ flex: 1 }}
                  onClick={() => setConfirmDel(null)}
                >
                  キャンセル
                </button>
                <button
                  className="rc-btn danger"
                  style={{ flex: 1 }}
                  onClick={() => {
                    removeSpecial(confirmDel);
                    setConfirmDel(null);
                  }}
                >
                  削除する
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {edit && draft && (
        <div className="rc-modal-ov" onClick={closeEdit}>
          <div className="rc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mh">
              点呼表の設定：
              {edit._isSpecial ? draft.title || edit.title : edit.display_title}
              <span className="x" onClick={closeEdit}>
                ✕
              </span>
            </div>
            <div className="mb">
              {edit._isSpecial && (
                <>
                  <label className="a-label">種類</label>
                  <div className="adm-seg">
                    {ENTRY_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        className={draft.entry_type === t.value ? "on" : ""}
                        onClick={() =>
                          setDraft((d) => ({ ...d, entry_type: t.value }))
                        }
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <label className="a-label">表示名</label>
                  <input
                    type="text"
                    value={draft.title}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, title: e.target.value }))
                    }
                  />
                  {draft.entry_type === "dance" ? (
                    <>
                      <label className="a-label">担当講師</label>
                      <select
                        value={draft.instructor_id}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, instructor_id: e.target.value }))
                        }
                      >
                        <option value="">担当講師を選択</option>
                        {instructors.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <>
                      <label className="a-label">所要時間（分・秒）</label>
                      <div className="rc-dur">
                        <input
                          type="number"
                          min="0"
                          placeholder="分"
                          value={draft.durMin}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, durMin: e.target.value }))
                          }
                        />
                        <span>:</span>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          placeholder="秒"
                          value={draft.durSec}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, durSec: e.target.value }))
                          }
                        />
                      </div>
                    </>
                  )}
                </>
              )}
              <button
                className="rc-btn pri"
                style={{ width: "100%", marginTop: 16 }}
                onClick={saveEdit}
                disabled={busy}
              >
                {busy ? "保存中…" : "保存する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
